import { useState, useEffect, useMemo } from "react";
import { Form, Tooltip, Avatar, Skeleton } from "antd";
import { InfoCircleOutlined, UserOutlined } from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

import { AppStrings } from "../../../utils/constants/AppStrings";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import {
  BRAND_ICONS_URLS,
  KPI_CARD_BACKGROUND,
} from "../../../utils/Constants";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import "./stylesWithdrawalsManualActions.css";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import {
  convertToUserTimezone,
  formatTimestampOrDisplay,
} from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import CustomSelect from "../../CommonComponents/CustomSelect/CustomSelect";
import { useGetAALWithdrawalsTable } from "../../../queries/agent-audit-log/withdrawals/useGetAALWithdrawalsTable";
import { useGetAALWithdrawalsKPIs } from "../../../queries/agent-audit-log/withdrawals/useGetAALWithdrawalsKPIs";
import { useDownloadAALWithdrawalsTable } from "../../../queries/agent-audit-log/withdrawals/useDownloadAALWithdrawalsTable";
import { useDownloadOnTrigger } from "../../../hooks/useDownloadOnTrigger";
import { toAalSort } from "../manualActionsTableShared";
import {
  useGetAALWithdrawalsTopDeclinedReasons,
  useGetAALWithdrawalsTopApprovedReasons,
} from "../../../queries/agent-audit-log/withdrawals/useGetAALWithdrawalsTopReasons";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";
import CustomInput from "../../CommonComponents/CustomInput/CustomInput";
import { filterOptionsFor } from "../../../queries/agent-audit-log/aalV2";
import { formatDurationSeconds } from "../../../utils/formatDurationSeconds";
import {
  REASONS_BAR_COLORS,
  WITHDRAWAL_APPROVED_TOP_REASONS_TITLE,
  WITHDRAWAL_DECLINE_TOP_REASONS_TITLE,
  formatWithdrawalTopReasonChartLabel,
} from "./withdrawalsManualActionsCharts";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

/**
 * Panel filter UX: payment_gateway, method, and status are intentionally single-select
 * (`CustomSelect` without `mode="multiple"`). Empty values use `null` for Ant Design Select + Form.
 * `toStringListField` still normalizes values when building API query params.
 */
const INITIAL_PANEL_FILTERS = {
  agent: "",
  role: null,
  username: "",
  payment_gateway: null,
  payment_method: null,
  status: null,
  subregion: "",
};

// AAL BE filter accepts only bank_ach / push_2_card; wire_transfer and cryptocurrency exist in METHOD_LABELS for future use.
const methodOptions = [
  { value: "bank_ach", label: "Bank ACH" },
  { value: "push_2_card", label: "Push 2 Card" },
  { value: "wire_transfer", label: "Wire Transfer" },
  { value: "cryptocurrency", label: "Cryptocurrency" },
];

const statusOptions = [
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "refund", label: "Refund" },
];

// PR #1097: Payment gateway feature temporarily hidden until backend is ready.
// Uncomment to re-enable the payment gateway filter and column.
// const paymentGatewayOptions = [
//   { value: "finix", label: "Finix" },
//   { value: "push_2_card", label: "Push 2 Card" },
//   { value: "bank_ach", label: "Bank ACH" },
//   { value: "wire_transfer", label: "Wire Transfer" },
//   { value: "cryptocurrency", label: "Cryptocurrency" },
// ];

function sortFieldFromSorter(field) {
  if (field === undefined || field === null) return null;
  if (Array.isArray(field)) {
    const first = field[0];
    return first !== undefined && first !== null ? String(first) : null;
  }
  return String(field);
}

function camelToSnakeCase(s) {
  return s.replace(/([A-Z])/g, "_$1").toLowerCase();
}

/**
 * Maps Ant Design column sort `field` to backend `sort_by`.
 *
 * The BE expects snake_case sort keys (e.g. `request_date`,
 * `withdrawals_approved_count`). Sending the raw camelCase `dataIndex`
 * (e.g. `lastDeposit`, `withdrawalsApproved`) makes the BE either crash or
 * silently ignore the sort — which produced the bug where Agent Performance
 * sorts misbehaved and Last Deposit was effectively unsortable.
 *
 * Strategy:
 * 1. Explicit overrides for fields whose BE name does NOT trivially derive
 *    from a camelCase → snake_case conversion (suffixes like `_count` or
 *    `_amount` that the FE column key omits).
 * 2. Fallback: generic camelCase → snake_case for everything else, so any
 *    column that gains `sorter: true` in the future works without touching
 *    this map (provided the BE name follows the trivial conversion).
 *
 * Snake_case targets were inferred from the canonical (first) keys used in
 * `normalizeWithdrawalRow` / `normalizeWithdrawalsApvRow` for each column.
 */
export function withdrawalsSortFieldForApi(field) {
  if (!field) return field;
  if (field === "lastDeposit" || field === "subregion") return null;
  const overrides = {
    // Detail table
    requestDate: "date",
    // Agent performance — BE counter columns use the `_count` suffix
    withdrawalsApproved: "withdrawals_approved_count",
    withdrawalsDeclined: "withdrawals_declined_count",
    withdrawalsReviewed: "withdrawals_reviewed_count",
  };
  if (Object.prototype.hasOwnProperty.call(overrides, field)) return overrides[field];
  return camelToSnakeCase(field);
}

function statusColorForWithdrawal(status) {
  const s = String(status ?? "").toLowerCase();
  if (s.includes("approv")) return "#71BF8A";
  if (s.includes("declin")) return "#E74C3C";
  if (s.includes("pending")) return "#F97316";
  return "#6B7280";
}

function formatUsernameDisplay(name) {
  if (name == null || name === "") return "";
  const s = String(name).trim();
  return s.startsWith("@") ? s : `@${s}`;
}

/** Table API returns lowercase/snake status values (e.g. `declined`); badge uses sentence case. */
function formatWithdrawalTableStatus(statusRaw) {
  const s = String(statusRaw ?? "").trim();
  if (!s) return "";
  const key = s.toLowerCase().replace(/\s+/g, "_");
  const map = {
    approved: "Approved",
    declined: "Declined",
    refund: "Refund",
  };
  if (map[key]) return map[key];
  return s
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const METHOD_LABELS = {
  push_2_card: "Push 2 Card",
  bank_ach: "Bank ACH",
  wire_transfer: "Wire Transfer",
  cryptocurrency: "Cryptocurrency",
};

const GATEWAY_LABELS = {
  finix: "Finix",
  push_2_card: "Push 2 Card",
  bank_ach: "Bank ACH",
  wire_transfer: "Wire Transfer",
  cryptocurrency: "Cryptocurrency",
};

function formatMethodLabel(v) {
  const s = String(v ?? "").trim();
  if (!s) return s;
  return METHOD_LABELS[s] ?? s;
}

function formatGatewayLabel(v) {
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  if (!s) return "";
  return GATEWAY_LABELS[s] ?? String(v).trim();
}

function normalizeWithdrawalRow(row, index) {
  const r = row;
  const statusForColor = r.transaction_status ?? r.status ?? "";
  const statusSource =
    typeof statusForColor === "string"
      ? statusForColor
      : String(statusForColor ?? "");
  const statusLabel = formatWithdrawalTableStatus(statusSource);
  const rawReason = r.review_reason ?? r.reason;
  const reasonLabel =
    rawReason == null || String(rawReason).trim() === ""
      ? ""
      : formatWithdrawalTopReasonChartLabel(rawReason);

  return {
    ...r,
    key: r.transaction_id ?? r.event_id ?? `w-${index}`,
    // Prefer UTC companions (*_timestamp / ISO). Display strings like
    // request_date are kept only as passthrough fallbacks in the cell render.
    requestDate: r.request_date_timestamp ?? r.request_at ?? undefined,
    requestDateDisplay: r.request_date,
    reviewTime: r.review_start_timestamp ?? undefined,
    reviewTimeDisplay: r.review_time ?? r.review_start,
    decisionTime:
      r.time_of_decision_timestamp ?? r.review_decision_timestamp ?? undefined,
    decisionTimeDisplay: r.decision_time,
    username: r.username ?? r.target_username,
    user_id: r.user_id ?? r.target_user_id,
    transactionId: r.transaction_id,
    lastDeposit: r.last_deposit_amount ?? r.last_deposit,
    lastDepositDate: r.last_deposit_date_timestamp ?? undefined,
    lastDepositDateDisplay: r.last_deposit_date,
    amount: r.withdrawal_amount ?? r.amount,
    payment_gateway: formatGatewayLabel(r.payment_gateway),
    method: formatMethodLabel(r.method ?? r.withdrawal_method),
    processedBy:
      r.processed_by_email ??
      r.agent_email ??
      r.pam_agent_email ??
      r.reviewed_by,
    processedByDate: r.reviewed_at ?? r.processed_at,
    agent_id: r.agent_id,
    status: statusLabel,
    statusColor: r.status_color ?? statusColorForWithdrawal(statusSource),
    reason: reasonLabel,
    subregion: r.target_user_subregion ?? r.subregion,
  };
}

function normalizeWithdrawalsApvRow(row, index) {
  const r = row;
  const reqToDecSec = r.avg_request_to_decision_seconds;
  const reqToDecPct = r.avg_request_to_decision_pct_change;
  const revToDecSec = r.avg_review_start_to_decision_seconds;
  const revToDecPct = r.avg_review_start_to_decision_pct_change;
  return {
    ...r,
    key: r.agent_id ?? r.pam_agent_id ?? String(index),
    agent: r.agent_email ?? r.pam_agent ?? "",
    withdrawalsAmountApproved: Number(
      r.withdrawals_amount_approved ??
        r.total_withdrawal_amount_approved ??
        r.approved_amount ??
        0,
    ),
    withdrawalsAmountApprovedPercent: Number(
      r.withdrawals_amount_approved_pct_change ??
        r.total_withdrawal_amount_approved_pct_change ??
        0,
    ),
    withdrawalsApproved: Number(
      r.withdrawals_approved_count ??
        r.withdrawals_approved ??
        r.approved_count ??
        0,
    ),
    withdrawalsApprovedPercent: Number(r.withdrawals_approved_pct_change ?? 0),
    withdrawalsDeclined: Number(
      r.withdrawals_declined_count ??
        r.withdrawals_declined ??
        r.declined_count ??
        0,
    ),
    withdrawalsDeclinedPercent: Number(r.withdrawals_declined_pct_change ?? 0),
    withdrawalsReviewed: Number(
      r.withdrawals_reviewed_count ??
        r.withdrawals_reviewed ??
        r.reviewed_count ??
        0,
    ),
    withdrawalsReviewedPercent: Number(r.withdrawals_reviewed_pct_change ?? 0),
    avgRequestToDecisionDisplay: formatDurationSeconds(reqToDecSec),
    avgRequestToDecisionPercent:
      reqToDecPct != null && !Number.isNaN(Number(reqToDecPct))
        ? Number(reqToDecPct)
        : null,
    avgReviewToDecisionDisplay: formatDurationSeconds(revToDecSec),
    avgReviewToDecisionPercent:
      revToDecPct != null && !Number.isNaN(Number(revToDecPct))
        ? Number(revToDecPct)
        : null,
    role: r.agent_role_id ?? r.role ?? "",
  };
}

function kpiNumber(obj, keys) {
  if (!obj) return 0;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && v !== "") return Number(v);
  }
  return 0;
}

function kpiPercent(obj, keys) {
  if (!obj) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

function kpiText(obj, keys) {
  if (!obj) return "—";
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return "—";
}

function kpiDurationDisplay(obj, secondsKeys, legacyTextKeys) {
  if (!obj) return "—";
  for (let i = 0; i < secondsKeys.length; i += 1) {
    const k = secondsKeys[i];
    const v = obj[k];
    if (v != null && v !== "" && Number.isFinite(Number(v))) {
      return formatDurationSeconds(Number(v));
    }
  }
  return kpiText(obj, legacyTextKeys);
}

function buildHorizontalBarChartData(rows) {
  const labels = rows.map((r) => formatWithdrawalTopReasonChartLabel(r.reason));
  const data = rows.map((r) => Number(r.transactions_count ?? 0));
  const colors = rows.map(
    (_, i) => REASONS_BAR_COLORS[i % REASONS_BAR_COLORS.length],
  );
  return {
    labels,
    datasets: [
      {
        label: "Count",
        data,
        backgroundColor: colors.map((c) => c.bg),
        borderColor: colors.map((c) => c.border),
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 18,
      },
    ],
  };
}

const horizontalBarOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: "rgba(255, 255, 255, 0.06)" },
      ticks: {
        color: "#9ca3af",
        font: { size: 11, family: "Poppins-regular" },
      },
    },
    y: {
      grid: { display: true },
      ticks: {
        color: "#92929E",
        font: { size: 12, family: "Poppins-regular" },
      },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#25252F",
      titleFont: { size: 13, family: "Poppins-regular", weight: "600" },
      bodyFont: { size: 12, family: "Poppins-regular" },
      padding: 12,
    },
  },
};

function WithdrawalsManualActions({
  agentPerformanceView = false,
  showFilters = false,
  dates,
  downloadTrigger = 0,
}) {
  const [form] = Form.useForm();
  const watchedAgent = Form.useWatch("agent", form) ?? "";
  const watchedRole = Form.useWatch("role", form);
  const watchedUsername = Form.useWatch("username", form) ?? "";
  const watchedPaymentGateway = Form.useWatch("payment_gateway", form);
  const watchedPaymentMethod = Form.useWatch("payment_method", form);
  const watchedStatus = Form.useWatch("status", form);
  const watchedSubregion = Form.useWatch("subregion", form) ?? "";

  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [agentFieldSeq, setAgentFieldSeq] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_PANEL_FILTERS);
  const [selectedRole, setSelectedRole] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const start_date = dates?.[0]?.utc?.().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc?.().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const hasFiltersFilled = useMemo(() => {
    void agentFieldSeq;
    const agentFromForm = String(form.getFieldValue("agent") ?? "").trim();
    const agentFilled =
      agentFromForm !== "" ||
      Boolean(watchedAgent && String(watchedAgent).trim() !== "");
    const roleFilled = watchedRole != null && watchedRole !== "";
    const usernameFilled = (watchedUsername ?? "").trim() !== "";
    const pgFilled =
      watchedPaymentGateway != null && watchedPaymentGateway !== "";
    const methodFilled =
      watchedPaymentMethod != null && watchedPaymentMethod !== "";
    const statusFilled = watchedStatus != null && watchedStatus !== "";
    const subregionFilled = String(watchedSubregion).trim() !== "";
    return (
      agentFilled ||
      roleFilled ||
      usernameFilled ||
      pgFilled ||
      methodFilled ||
      statusFilled ||
      subregionFilled
    );
  }, [
    agentFieldSeq,
    watchedAgent,
    watchedRole,
    watchedUsername,
    watchedPaymentGateway,
    watchedPaymentMethod,
    watchedStatus,
    watchedSubregion,
    form,
  ]);

  const tableFilters = useMemo(() => {
    const base = {
      view: listView,
      start_date: start_date ?? "",
      end_date: end_date ?? "",
      page: currentPage,
      size: pageSize,
      sort: toAalSort(withdrawalsSortFieldForApi(sortField), sortOrder),
      ...(agentPerformanceView
        ? selectedRole.length > 0 && { agent_role_id: selectedRole[0] }
        : appliedFilters.role && { agent_role_id: appliedFilters.role }),
      ...(!agentPerformanceView &&
        appliedFilters.agent && {
          agent_id: appliedFilters.agent,
        }),
      ...(!agentPerformanceView &&
        appliedFilters.payment_gateway && {
          payment_gateway: appliedFilters.payment_gateway,
        }),
      ...(!agentPerformanceView &&
        appliedFilters.payment_method && {
          payment_method: appliedFilters.payment_method,
        }),
      ...(!agentPerformanceView &&
        appliedFilters.status && { status: appliedFilters.status }),
      ...(!agentPerformanceView &&
        appliedFilters.subregion && { subregion: appliedFilters.subregion }),
      ...(!agentPerformanceView &&
        appliedFilters.username && { username: appliedFilters.username }),
    };
    return base;
  }, [
    listView,
    start_date,
    end_date,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    agentPerformanceView,
    selectedRole,
    appliedFilters,
  ]);

  const kpiFilters = useMemo(
    () => ({
      start_date: start_date ?? "",
      end_date: end_date ?? "",
    }),
    [start_date, end_date],
  );

  const chartFilters = useMemo(() => {
    if (agentPerformanceView) {
      return {
        start_date: start_date ?? "",
        end_date: end_date ?? "",
        ...(selectedRole.length > 0 && { agent_role_id: selectedRole[0] }),
      };
    }
    return {
      start_date: start_date ?? "",
      end_date: end_date ?? "",
      ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
      ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
    };
  }, [
    agentPerformanceView,
    start_date,
    end_date,
    selectedRole,
    appliedFilters.agent,
    appliedFilters.role,
  ]);

  const {
    items: tableItems,
    total: totalResults,
    filters: tableFiltersMeta,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALWithdrawalsTable(tableFilters);

  const serverMethodOptions = filterOptionsFor(
    tableFiltersMeta ?? [],
    "payment_method",
  );
  const serverStatusOptions = filterOptionsFor(
    tableFiltersMeta ?? [],
    "status",
  ).map((option) => ({
    ...option,
    label: formatWithdrawalTableStatus(option.value),
  }));
  const effectiveMethodOptions = serverMethodOptions.length
    ? serverMethodOptions
    : methodOptions;
  const effectiveStatusOptions = serverStatusOptions.length
    ? serverStatusOptions
    : statusOptions;

  const { data: kpiData, isLoading: isKpiLoading } =
    useGetAALWithdrawalsKPIs(kpiFilters);

  const { data: declinedReasons = [], isLoading: isDeclinedReasonsLoading } =
    useGetAALWithdrawalsTopDeclinedReasons(chartFilters, {
      enabled: agentPerformanceView,
    });

  const { data: approvedReasons = [], isLoading: isApprovedReasonsLoading } =
    useGetAALWithdrawalsTopApprovedReasons(chartFilters, {
      enabled: agentPerformanceView,
    });

  const { data: roles } = useGetRoles();
  const { data: staffData } = useGetStaff({
    page_size: 100,
  });

  const agentOptions = (staffData?.items ?? []).map((s) => ({
    value: s.email,
    label: s.email,
    id: s.id,
  }));

  const roleOptions = (roles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const roleById = Object.fromEntries(
    (roles ?? []).map((r) => [
      r.id,
      { name: r.name, color: r.color ?? "#6b7280" },
    ]),
  );

  useEffect(() => {
    if (agentPerformanceView) {
      setCurrentPage(1);
    }
  }, [selectedRole, agentPerformanceView]);

  useEffect(() => {
    if (!agentPerformanceView) {
      setSelectedRole([]);
    }
    setSortField(null);
    setSortOrder(null);
    setCurrentPage(1);
  }, [agentPerformanceView]);

  const { start: downloadTable } = useDownloadAALWithdrawalsTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(agentPerformanceView
      ? selectedRole.length > 0
        ? { agent_role_id: selectedRole[0] }
        : {}
      : {
          ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
          ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
          ...(appliedFilters.username && {
            username: appliedFilters.username,
          }),
          ...(appliedFilters.payment_gateway && {
            payment_gateway: appliedFilters.payment_gateway,
          }),
          ...(appliedFilters.payment_method && {
            payment_method: appliedFilters.payment_method,
          }),
          ...(appliedFilters.status && { status: appliedFilters.status }),
          ...(appliedFilters.subregion && {
            subregion: appliedFilters.subregion,
          }),
        }),
  }));

  const tableRows = useMemo(
    () => tableItems.map((row, index) => normalizeWithdrawalRow(row, index)),
    [tableItems],
  );

  const apvDataSource = useMemo(
    () =>
      tableItems.map((row, index) => normalizeWithdrawalsApvRow(row, index)),
    [tableItems],
  );

  const declinedChartData = useMemo(
    () => buildHorizontalBarChartData(declinedReasons),
    [declinedReasons],
  );

  const approvedChartData = useMemo(
    () => buildHorizontalBarChartData(approvedReasons),
    [approvedReasons],
  );

  const currencyImageSrc =
    (process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL || "") +
    BRAND_ICONS_URLS.primaryCurrencySymbol;

  const formatNumber = (value, percentage = false) =>
    new Intl.NumberFormat("en-US", {
      style: percentage ? "percent" : "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));

  const handleClearFilters = () => {
    form.resetFields();
    // `agent` is not wrapped in `Form.Item`; only mounted fields are reset by `resetFields`.
    form.setFieldValue("agent", "");
    setAutoCompleteKey((k) => k + 1);
    setAgentFieldSeq((n) => n + 1);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  };

  const handleApply = () => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      username: values.username ?? "",
      payment_gateway: values.payment_gateway ?? null,
      payment_method: values.payment_method ?? null,
      status: values.status ?? null,
      subregion: values.subregion ?? "",
    });
    setAgentFieldSeq((n) => n + 1);
    setCurrentPage(1);
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = sortFieldFromSorter(s?.field);
    if (field) {
      setSortField(field);
      setSortOrder(
        s.order === "ascend" || s.order === "descend" ? s.order : null,
      );
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const renderPercent = (percent) => {
    if (percent == null || Number.isNaN(Number(percent))) return null;
    const n = Number(percent);
    const sign = n >= 0 ? "+" : "";
    return (
      <span
        style={{
          marginLeft: "4px",
          color: getColorBasedOnValue(n),
          fontSize: "11px",
        }}
      >
        {sign}
        {n.toFixed(1)}%
      </span>
    );
  };

  const k = kpiData ?? {};

  const columns = [
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          Request Date
          <Tooltip title={AppStrings.REQUEST_DATE_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "requestDate",
      key: "requestDate",
      sorter: true,
      showSorterTooltip: false,
      width: 200,
      render: (text, record) => {
        const formatted = formatTimestampOrDisplay(
          text,
          record.requestDateDisplay,
        );
        return formatted ? (
          <span>{formatted}</span>
        ) : (
          <span style={{ color: "#6B7280" }}>—</span>
        );
      },
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          Review Time
          <Tooltip title={AppStrings.REVIEW_TIME_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "reviewTime",
      key: "reviewTime",
      showSorterTooltip: false,
      width: 150,
      render: (text, record) => {
        const formatted = formatTimestampOrDisplay(
          text,
          record.reviewTimeDisplay,
        );
        return formatted ? (
          <span>{formatted}</span>
        ) : (
          <span style={{ color: "#6B7280" }}>—</span>
        );
      },
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          Time of Decision
          <Tooltip title={AppStrings.TIME_OF_DECISION_TOOLTIP}>
            <InfoCircleOutlined
              style={{
                color: "var(--grey-400)",
                fontSize: "12px",
                cursor: "help",
              }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "decisionTime",
      key: "decisionTime",
      showSorterTooltip: false,
      width: 150,
      render: (text, record) => {
        const formatted = formatTimestampOrDisplay(
          text,
          record.decisionTimeDisplay,
        );
        return formatted ? (
          <span>{formatted}</span>
        ) : (
          <span style={{ color: "#6B7280" }}>—</span>
        );
      },
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      showSorterTooltip: false,
      width: 220,
      render: (text, record) => (
        <div className="withdrawals-manual-actions-username-cell">
          <Avatar
            size={20}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#6B7280" }}
            className="withdrawals-manual-actions-avatar"
          />
          <a
            href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.user_id ?? ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="withdrawals-manual-actions-hoverable"
          >
            {formatUsernameDisplay(text)}
          </a>
        </div>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          Last Deposit
          <Tooltip title="User last deposit before withdrawal request">
            <InfoCircleOutlined
              style={{ color: "var(--grey-400)", fontSize: "12px" }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: "lastDeposit",
      key: "lastDeposit",
      width: 200,
      render: (text, record) => (
        <div className="withdrawals-manual-actions-deposit-cell">
          <span>{formatNumber(text)}</span>
          <span className="withdrawals-manual-actions-date">
            {formatTimestampOrDisplay(
              record.lastDepositDate,
              record.lastDepositDateDisplay,
              "MMM DD, HH:mm",
            )}
          </span>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      width: 200,
      render: (text) => (
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          <img
            src={currencyImageSrc}
            alt=""
            style={{ width: "14px", height: "14px" }}
          />
          {formatNumber(text)}
        </span>
      ),
    },
    {
      title: AppStrings.PAYMENT_GATEWAY,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      showSorterTooltip: false,
      width: 200,
    },
    {
      title: AppStrings.STATE,
      dataIndex: "subregion",
      key: "subregion",
      width: 160,
      showSorterTooltip: false,
      render: (_, record) => {
        const v = record?.subregion;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: "Processed By",
      dataIndex: "processedBy",
      key: "processedBy",
      showSorterTooltip: false,
      width: 220,
      render: (text, record) => (
        <div className="withdrawals-manual-actions-processed-cell">
          <a
            href={getManualActionsStaffManagementHref(record.agent_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="withdrawals-manual-actions-processed-by withdrawals-manual-actions-hoverable"
          >
            {text}
          </a>
          <span className="withdrawals-manual-actions-date">
            {record.processedByDate
              ? convertToUserTimezone(record.processedByDate, "MMM DD, HH:mm")
              : ""}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      showSorterTooltip: false,
      width: 200,
      render: (text, record) => {
        if (!text || text === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return (
          <span
            className="withdrawals-manual-actions-status-badge"
            style={{ backgroundColor: record.statusColor }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      showSorterTooltip: false,
      width: 200,
      render: (text) => {
        if (!text || text === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{text}</span>;
      },
    },
  ];

  const agentPerformanceColumns = [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent",
      key: "agent",
      width: 240,
      render: (text, record) => (
        <a
          href={getManualActionsStaffManagementHref(record.agent_id)}
          target="_blank"
          rel="noopener noreferrer"
          className="withdrawals-manual-actions-hoverable"
        >
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.TOTAL_WITHDRAWAL_AMOUNT_APPROVED,
      dataIndex: "withdrawalsAmountApproved",
      key: "withdrawalsAmountApproved",
      width: 260,
      sorter: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img
            src={currencyImageSrc}
            alt=""
            style={{ width: "14px", height: "14px" }}
          />
          {formatNumber(text || 0, false)}
          {renderPercent(record.withdrawalsAmountApprovedPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_APPROVED,
      dataIndex: "withdrawalsApproved",
      key: "withdrawalsApproved",
      width: 220,
      sorter: true,
      render: (text, record) => (
        <span>
          {text}
          {renderPercent(record.withdrawalsApprovedPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_DECLINED,
      dataIndex: "withdrawalsDeclined",
      key: "withdrawalsDeclined",
      width: 220,
      sorter: true,
      render: (text, record) => (
        <span>
          {text}
          {renderPercent(record.withdrawalsDeclinedPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.NUMBER_OF_WITHDRAWALS_REVIEWED_SHORT,
      dataIndex: "withdrawalsReviewed",
      key: "withdrawalsReviewed",
      width: 220,
      sorter: true,
      render: (text, record) => (
        <span>
          {text}
          {renderPercent(record.withdrawalsReviewedPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.AVG_TIME_REQUEST_TO_DECISION,
      dataIndex: "avgRequestToDecisionDisplay",
      key: "avgRequestToDecisionDisplay",
      width: 220,
      render: (text, record) => (
        <span>
          {text}
          {renderPercent(record.avgRequestToDecisionPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.AVG_TIME_REVIEW_START_TO_DECISION,
      dataIndex: "avgReviewToDecisionDisplay",
      key: "avgReviewToDecisionDisplay",
      width: 260,
      render: (text, record) => (
        <span>
          {text}
          {renderPercent(record.avgReviewToDecisionPercent)}
        </span>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "role",
      key: "role",
      width: 170,
      render: (value) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
  ];

  const tableLoading = isTableLoading || isTableFetching;

  const approvedAmount = kpiNumber(k, [
    "total_withdrawal_amount_reviewed",
    "total_withdrawal_amount_approved",
    "approved_withdrawal_amount",
    "total_approved_amount",
  ]);
  const approvedAmountPct = kpiPercent(k, [
    "total_withdrawal_amount_reviewed_pct_change",
    "total_withdrawal_amount_approved_pct_change",
    "approved_withdrawal_amount_pct_change",
  ]);

  const reviewedCount = kpiNumber(k, [
    "withdrawals_reviewed_count",
    "withdrawals_reviewed",
    "reviewed_count",
  ]);
  const reviewedPct = kpiPercent(k, [
    "withdrawals_reviewed_count_pct_change",
    "withdrawals_reviewed_pct_change",
    "reviewed_count_pct_change",
  ]);

  const approvedCount = kpiNumber(k, [
    "withdrawals_approved_count",
    "withdrawals_approved",
    "approved_count",
  ]);
  const approvedCountPct = kpiPercent(k, [
    "withdrawals_approved_count_pct_change",
    "withdrawals_approved_pct_change",
    "approved_count_pct_change",
  ]);

  const declinedCount = kpiNumber(k, [
    "withdrawals_declined_count",
    "withdrawals_declined",
    "declined_count",
  ]);
  const declinedPct = kpiPercent(k, [
    "withdrawals_declined_count_pct_change",
    "withdrawals_declined_pct_change",
    "declined_count_pct_change",
  ]);

  const avgRequestToDecision = kpiDurationDisplay(
    k,
    ["avg_request_to_decision_seconds"],
    [],
  );
  const avgRequestToDecisionPct = kpiPercent(k, [
    "avg_request_to_decision_pct_change",
  ]);

  const avgReviewToDecision = kpiDurationDisplay(
    k,
    ["avg_review_start_to_decision_seconds"],
    [],
  );
  const avgReviewToDecisionPct = kpiPercent(k, [
    "avg_review_start_to_decision_pct_change",
  ]);

  return (
    <div className="withdrawals-manual-actions-container">
      <Form form={form} initialValues={INITIAL_PANEL_FILTERS}>
        <div
          className="withdrawals-manual-actions-selectors"
          style={{
            display: showFilters && !agentPerformanceView ? "flex" : "none",
          }}
        >
          <div className="withdrawals-manual-actions-selector">
            <span>{AppStrings.PAM_AGENT}</span>
            <CustomAutoCompleteControlled
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={(id) => {
                form.setFieldValue("agent", id);
                setAgentFieldSeq((n) => n + 1);
              }}
              clearOptions={() => {
                form.setFieldValue("agent", "");
                setAgentFieldSeq((n) => n + 1);
              }}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          {/* PR #1097: Payment gateway filter temporarily hidden until backend is ready. */}
          {/* <div className="withdrawals-manual-actions-selector">
            <span>{AppStrings.PAYMENT_GATEWAY}</span>
            <Form.Item name="payment_gateway" noStyle>
              <CustomSelect
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={paymentGatewayOptions}
              />
            </Form.Item>
          </div> */}
          <div className="withdrawals-manual-actions-selector">
            <span>Method</span>
            <Form.Item name="payment_method" noStyle>
              <CustomSelect
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={effectiveMethodOptions}
              />
            </Form.Item>
          </div>
          <div className="withdrawals-manual-actions-selector">
            <span>Status</span>
            <Form.Item name="status" noStyle>
              <CustomSelect
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={effectiveStatusOptions}
              />
            </Form.Item>
          </div>
          <div className="withdrawals-manual-actions-selector">
            <span>{AppStrings.STATE}</span>
            <Form.Item name="subregion" noStyle>
              <CustomInput
                placeholder={AppStrings.STATE}
                aria-label={AppStrings.STATE}
              />
            </Form.Item>
          </div>
          <div className="withdrawals-manual-actions-selector">
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <CustomInput placeholder={AppStrings.USERNAME} />
            </Form.Item>
          </div>
          <div className="withdrawals-manual-actions-selector">
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <CustomSelect
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={roleOptions}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className="withdrawals-manual-actions-selector-action-buttons"
        style={{
          display: showFilters && !agentPerformanceView ? "flex" : "none",
        }}
      >
        <button
          type="button"
          className={`clear ${hasFiltersFilled ? "dirty" : ""}`}
          onClick={handleClearFilters}
        >
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          type="button"
          className={`apply ${hasFiltersFilled ? "dirty" : ""}`}
          onClick={handleApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>
      <div
        className="withdrawals-manual-actions-kpi-cards"
        style={{ display: !agentPerformanceView ? "flex" : "none" }}
      >
        <KpiCard
          title={AppStrings.TOTAL_WITHDRAWAL_AMOUNT_APPROVED}
          value={approvedAmount}
          percentage={approvedAmountPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
        />
        <KpiCard
          title={AppStrings.NUMBER_OF_WITHDRAWALS_REVIEWED_SHORT}
          value={reviewedCount}
          percentage={reviewedPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
          maximumFractionDigits={0}
        />
        <KpiCard
          title={AppStrings.NUMBER_OF_WITHDRAWALS_APPROVED}
          value={approvedCount}
          percentage={approvedCountPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
          maximumFractionDigits={0}
        />
        <KpiCard
          title={AppStrings.NUMBER_OF_WITHDRAWALS_DECLINED}
          value={declinedCount}
          percentage={declinedPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
          maximumFractionDigits={0}
        />
        <KpiCard
          title={AppStrings.AVG_TIME_REQUEST_TO_DECISION}
          value={avgRequestToDecision}
          percentage={avgRequestToDecisionPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
          displayAsRawText
        />
        <KpiCard
          title={AppStrings.AVG_TIME_REVIEW_START_TO_DECISION}
          value={avgReviewToDecision}
          percentage={avgReviewToDecisionPct ?? undefined}
          hugeValue={false}
          backgroundColor={KPI_CARD_BACKGROUND.DARK}
          isApiRunning={isKpiLoading}
          displayAsRawText
        />
      </div>
      <div className="withdrawals-manual-actions-table-container">
        {agentPerformanceView ? (
          <AgentPerformanceView
            columns={agentPerformanceColumns}
            handleTableChange={handleTableChange}
            filteredData={apvDataSource}
            roleOptions={roleOptions}
            selectedRoles={selectedRole}
            onRoleChange={setSelectedRole}
            scrollX={2420}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalResults,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            isLoading={tableLoading}
          />
        ) : (
          <DataTable
            columns={columns}
            dataSource={tableRows}
            loading={tableLoading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalResults,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            onChange={handleTableChange}
            rowKey="key"
            scroll={{ x: 2200, y: 500 }}
          />
        )}
      </div>
      {agentPerformanceView ? (
        <div
          className="withdrawals-manual-charts-row"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 24,
          }}
        >
          <div
            className="withdrawals-manual-chart-container"
            style={{ width: "49%" }}
          >
            <div className="withdrawals-decline-reasons-chart-header">
              <h3 className="withdrawals-decline-reasons-chart-title">
                {WITHDRAWAL_DECLINE_TOP_REASONS_TITLE}
              </h3>
            </div>
            <div
              className="withdrawals-decline-reasons-chart-body"
              style={{ minHeight: 280 }}
            >
              {isDeclinedReasonsLoading ? (
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 6 }}
                  style={{ minHeight: 280 }}
                />
              ) : declinedReasons.length === 0 ? (
                <div style={{ padding: 24, color: "var(--grey-400)" }}>
                  No data
                </div>
              ) : (
                <Bar data={declinedChartData} options={horizontalBarOptions} />
              )}
            </div>
          </div>
          <div
            className="withdrawals-manual-chart-container"
            style={{ width: "49%" }}
          >
            <div className="withdrawals-decline-reasons-chart-header">
              <h3 className="withdrawals-decline-reasons-chart-title">
                {WITHDRAWAL_APPROVED_TOP_REASONS_TITLE}
              </h3>
            </div>
            <div
              className="withdrawals-decline-reasons-chart-body"
              style={{ minHeight: 280 }}
            >
              {isApprovedReasonsLoading ? (
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 6 }}
                  style={{ minHeight: 280 }}
                />
              ) : approvedReasons.length === 0 ? (
                <div style={{ padding: 24, color: "var(--grey-400)" }}>
                  No data
                </div>
              ) : (
                <Bar data={approvedChartData} options={horizontalBarOptions} />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default WithdrawalsManualActions;
